// src/components/upload/PhotoUpload.tsx
//
// Photo upload UI for the Gallery page. Signed-in employees only.
// Drag-drop + file picker, year selector, per-file caption + progress,
// batch submit. Mobile-friendly (touch targets, no hover-only states).
//
// Flow:
//   1. POST /api/me/photos/sas → write SAS URLs per file
//   2. PUT each file directly to its blob URL (parallel, concurrency=3)
//   3. POST /api/me/photos/register → write Cosmos docs + thumbnails
//   4. Show success summary; pending photos go to /admin/photos for review

import { useState, useRef, useEffect } from "react";
import { useAuth } from "../../auth/useAuth";

interface PhotoUploadProps {
  defaultYear:    string;
  availableYears: string[];
}

interface PendingFile {
  id:        string;
  file:      File;
  status:    "pending" | "uploading" | "uploaded" | "failed";
  progress:  number;        // 0..1
  caption?:  string;
  error?:    string;
  preview?:  string;        // object URL for image thumbnail
  blobName?: string;        // assigned by SAS response
}

interface SasUpload {
  originalFilename: string;
  contentType:      string;
  blobName:         string;
  uploadUrl:        string;
}

const MAX_FILES           = 50;
const MAX_SIZE            = 25 * 1024 * 1024;
const MAX_CAPTION_LEN     = 250;
const UPLOAD_CONCURRENCY  = 3;
const ACCEPT              = "image/jpeg,image/png,image/heic,image/heif,image/webp";
const PREVIEW_MIMES       = new Set(["image/jpeg", "image/png", "image/webp"]); // browser-renderable

export default function PhotoUpload({ defaultYear, availableYears }: PhotoUploadProps) {
  const { getApiToken } = useAuth();
  const [isOpen,       setIsOpen]       = useState(false);
  const [year,         setYear]         = useState(defaultYear);
  const [files,        setFiles]        = useState<PendingFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging,   setIsDragging]   = useState(false);
  const [summary,      setSummary]      = useState<{ success: number; failed: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setYear(defaultYear); }, [defaultYear]);

  useEffect(() => () => {
    files.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── File ingestion ──────────────────────────────────────────────────────
  function addFiles(list: FileList | File[]) {
    const incoming = Array.from(list);
    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      alert(`Maximum ${MAX_FILES} files per batch — please remove some first.`);
      return;
    }

    const added: PendingFile[] = [];
    const skipped: string[] = [];

    for (const file of incoming.slice(0, remaining)) {
      if (file.size > MAX_SIZE) {
        skipped.push(`${file.name} — too large (max 25MB)`);
        continue;
      }
      if (!ACCEPT.split(",").includes(file.type)) {
        skipped.push(`${file.name} — type not supported (${file.type || "unknown"})`);
        continue;
      }
      added.push({
        id:       crypto.randomUUID(),
        file,
        status:   "pending",
        progress: 0,
        preview:  PREVIEW_MIMES.has(file.type) ? URL.createObjectURL(file) : undefined,
      });
    }

    if (incoming.length > remaining) {
      skipped.push(`${incoming.length - remaining} additional file(s) skipped — batch capped at ${MAX_FILES}`);
    }
    if (skipped.length > 0) alert("Some files were skipped:\n\n" + skipped.join("\n"));
    if (added.length > 0)  setFiles(prev => [...prev, ...added]);
  }

  function removeFile(id: string) {
    setFiles(prev => {
      const f = prev.find(x => x.id === id);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter(x => x.id !== id);
    });
  }

  function clearAll() {
    files.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
    setFiles([]);
    setSummary(null);
  }

  function updateCaption(id: string, value: string) {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, caption: value } : f));
  }

  // ── Drag-drop ───────────────────────────────────────────────────────────
  function handleDragOver(e: React.DragEvent) { e.preventDefault(); setIsDragging(true); }
  function handleDragLeave(e: React.DragEvent) { e.preventDefault(); setIsDragging(false); }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setIsDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  }

  // ── Per-file XHR upload (with progress) ─────────────────────────────────
  function uploadOne(pf: PendingFile, uploadUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
      xhr.setRequestHeader("Content-Type", pf.file.type);
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const progress = e.loaded / e.total;
        setFiles(prev => prev.map(x => x.id === pf.id ? { ...x, progress } : x));
      };
      xhr.onload  = () => xhr.status >= 200 && xhr.status < 300
                          ? resolve()
                          : reject(new Error(`Upload failed (HTTP ${xhr.status})`));
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(pf.file);
    });
  }

  async function withConcurrency<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
    const results: R[] = new Array(items.length);
    let cursor = 0;
    async function worker() {
      while (cursor < items.length) {
        const i = cursor++;
        results[i] = await fn(items[i]);
      }
    }
    await Promise.all(Array(Math.min(limit, items.length)).fill(0).map(worker));
    return results;
  }

  // ── Submit ──────────────────────────────────────────────────────────────
  async function submit() {
    if (files.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    setSummary(null);

    try {
      const token = await getApiToken();
      if (!token) { alert("Not signed in — please refresh."); setIsSubmitting(false); return; }

      const sasRes = await fetch("/api/me/photos/sas", {
        method:  "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body:    JSON.stringify({
          year,
          files: files.map(f => ({
            originalFilename: f.file.name,
            contentType:      f.file.type,
            size:             f.file.size,
          })),
        }),
      });

      if (!sasRes.ok) {
        const body = await sasRes.json().catch(() => ({}));
        alert(`Failed to start upload: ${body.error ?? sasRes.statusText}`);
        setIsSubmitting(false);
        return;
      }

      const { uploads } = await sasRes.json() as { uploads: SasUpload[] };

      const sasByName = new Map<string, SasUpload>();
      uploads.forEach(u => sasByName.set(u.originalFilename, u));

      const eligible = files.filter(f => sasByName.has(f.file.name));
      const ineligible = files.filter(f => !sasByName.has(f.file.name));

      setFiles(prev => prev.map(f =>
        sasByName.has(f.file.name)
          ? { ...f, status: "uploading", progress: 0 }
          : { ...f, status: "failed", error: "Server rejected" }
      ));

      type UploadOutcome = { pf: PendingFile; blobName?: string; ok: boolean };
      const outcomes = await withConcurrency<PendingFile, UploadOutcome>(eligible, UPLOAD_CONCURRENCY, async (pf) => {
        const u = sasByName.get(pf.file.name)!;
        try {
          await uploadOne(pf, u.uploadUrl);
          setFiles(prev => prev.map(x => x.id === pf.id
            ? { ...x, status: "uploaded", progress: 1, blobName: u.blobName }
            : x));
          return { pf, blobName: u.blobName, ok: true };
        } catch (err: any) {
          setFiles(prev => prev.map(x => x.id === pf.id
            ? { ...x, status: "failed", error: err?.message ?? "Upload failed" }
            : x));
          return { pf, ok: false };
        }
      });

      const successful = outcomes.filter(o => o.ok);
      let registeredCount = 0;

      if (successful.length > 0) {
        // Snapshot the latest captions at register time (in case user typed
        // captions while uploads were running).
        const latestFiles = await new Promise<PendingFile[]>((resolve) => {
          setFiles(prev => { resolve(prev); return prev; });
        });
        const captionById = new Map(latestFiles.map(f => [f.id, f.caption ?? ""]));

        const regRes = await fetch("/api/me/photos/register", {
          method:  "POST",
          headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
          body:    JSON.stringify({
            year,
            photos: successful.map(o => ({
              blobName:         o.blobName!,
              originalFilename: o.pf.file.name,
              contentType:      o.pf.file.type,
              caption:          (captionById.get(o.pf.id) ?? "").trim(),
            })),
          }),
        });

        if (regRes.ok) {
          const body = await regRes.json();
          registeredCount = (body.registered ?? []).length;
        } else {
          alert("Files uploaded but registration failed — please contact an admin.");
        }
      }

      const failedCount = ineligible.length + outcomes.filter(o => !o.ok).length + (successful.length - registeredCount);
      setSummary({ success: registeredCount, failed: failedCount });

      if (registeredCount > 0) {
        setFiles(prev => prev.filter(f => f.status === "failed"));
      }
    } catch (err: any) {
      alert(`Upload failed: ${err?.message ?? "Unknown error"}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  if (!isOpen) {
    return (
      <div className="upload-collapsed">
        <button className="upload-toggle" onClick={() => setIsOpen(true)}>
          + Add Photos
        </button>
        <style>{collapsedStyles}</style>
      </div>
    );
  }

  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);
  const canSubmit = files.length > 0 && !isSubmitting;

  return (
    <section className="upload-panel">
      <header className="upload-header">
        <div>
          <div className="upload-eyebrow">— Upload</div>
          <h2 className="upload-title">Add Photos</h2>
        </div>
        <button className="upload-close" onClick={() => { setIsOpen(false); clearAll(); }}>
          Close
        </button>
      </header>

      <div className="upload-controls">
        <label className="upload-year-label">
          <span>Year</span>
          <select value={year} onChange={(e) => setYear(e.target.value)} disabled={isSubmitting}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <div className="upload-meta">
          {files.length === 0
            ? <span>Up to {MAX_FILES} files · 25 MB each · JPEG, PNG, HEIC, WebP</span>
            : <span>{files.length} of {MAX_FILES} · {formatBytes(totalSize)} total</span>
          }
        </div>
      </div>

      <div
        className={`upload-dropzone ${isDragging ? "is-dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <div className="upload-dropzone-icon">📷</div>
        <div className="upload-dropzone-text">
          <strong>Tap to choose photos</strong>
          <span>or drag and drop here</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
      </div>

      {files.length > 0 && (
        <ul className="upload-file-list">
          {files.map(f => (
            <li key={f.id} className={`upload-file-item is-${f.status}`}>
              <div className="upload-thumb">
                {f.preview
                  ? <img src={f.preview} alt="" />
                  : <span>{(f.file.name.split(".").pop() || "?").toUpperCase()}</span>}
              </div>
              <div className="upload-info">
                <div className="upload-name">{f.file.name}</div>
                <div className="upload-sub">
                  {formatBytes(f.file.size)}
                  {f.status === "failed" && f.error && <span className="upload-err"> · {f.error}</span>}
                  {f.status === "uploaded" && <span className="upload-ok"> · uploaded</span>}
                </div>
                <input
                  className="upload-caption"
                  type="text"
                  placeholder="Add a caption (optional)"
                  value={f.caption ?? ""}
                  onChange={(e) => updateCaption(f.id, e.target.value)}
                  disabled={isSubmitting && (f.status === "uploading" || f.status === "uploaded")}
                  maxLength={MAX_CAPTION_LEN}
                />
                {f.status === "uploading" && (
                  <div className="upload-progress"><div style={{ width: `${Math.round(f.progress * 100)}%` }} /></div>
                )}
              </div>
              <button
                className="upload-remove"
                onClick={() => removeFile(f.id)}
                disabled={isSubmitting && f.status === "uploading"}
                aria-label={`Remove ${f.file.name}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {summary && (
        <div className={`upload-summary ${summary.failed === 0 ? "is-success" : "is-partial"}`}>
          {summary.failed === 0
            ? `✓ ${summary.success} photo${summary.success === 1 ? "" : "s"} uploaded — pending admin review.`
            : `${summary.success} succeeded, ${summary.failed} failed. Failed files remain in the list.`}
        </div>
      )}

      <div className="upload-actions">
        <button className="upload-submit" onClick={submit} disabled={!canSubmit}>
          {isSubmitting
            ? "Uploading…"
            : `Upload ${files.length || ""} Photo${files.length === 1 ? "" : "s"}`.trim()}
        </button>
        {files.length > 0 && !isSubmitting && (
          <button className="upload-clear" onClick={clearAll}>Clear All</button>
        )}
      </div>

      <style>{panelStyles}</style>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024)              return `${bytes} B`;
  if (bytes < 1024 * 1024)       return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const collapsedStyles = `
  .upload-collapsed { margin: 1rem 0 1.5rem; }
  .upload-toggle {
    background: var(--red); color: white;
    font-family: var(--font-display);
    text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; font-size: 0.9rem;
    padding: 0.85rem 1.6rem; border: none; border-radius: var(--radius);
    cursor: pointer; transition: background 0.15s; min-height: 44px;
  }
  .upload-toggle:hover, .upload-toggle:focus { background: var(--red-dark); }
`;

const panelStyles = `
  .upload-panel {
    background: var(--bg); border-top: 3px solid var(--red);
    padding: 2rem; margin: 1rem 0 2rem; border-radius: 0 0 var(--radius) var(--radius);
  }
  .upload-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: 1rem; margin-bottom: 1.5rem;
  }
  .upload-eyebrow {
    font-family: var(--font-display); text-transform: uppercase;
    letter-spacing: 0.35em; color: var(--red); font-size: 0.7rem; font-weight: 500;
  }
  .upload-title {
    font-family: var(--font-display); text-transform: uppercase;
    font-weight: 700; font-size: 1.6rem; margin: 0.25rem 0 0; letter-spacing: 0.02em;
  }
  .upload-close {
    background: transparent; border: 1px solid var(--border); border-radius: var(--radius);
    padding: 0.5rem 0.9rem;
    font-family: var(--font-display); text-transform: uppercase;
    font-size: 0.75rem; font-weight: 500; letter-spacing: 0.06em;
    color: var(--mid); cursor: pointer; min-height: 36px;
  }
  .upload-close:hover, .upload-close:focus { color: var(--text); border-color: var(--mid); }

  .upload-controls {
    display: flex; flex-wrap: wrap; gap: 1rem 2rem; align-items: center; margin-bottom: 1rem;
  }
  .upload-year-label {
    display: flex; align-items: center; gap: 0.6rem;
    font-family: var(--font-display); text-transform: uppercase;
    font-size: 0.8rem; letter-spacing: 0.06em; font-weight: 500;
  }
  .upload-year-label select {
    font-family: var(--font-sub); font-size: 1rem;
    padding: 0.5rem 0.75rem; border: 1px solid var(--border);
    border-radius: var(--radius); background: white; min-height: 40px;
  }
  .upload-meta { color: var(--mid); font-size: 0.85rem; }

  .upload-dropzone {
    background: white; border: 2px dashed var(--border); border-radius: var(--radius);
    padding: 2.5rem 1rem; text-align: center; cursor: pointer; transition: all 0.15s;
    display: flex; flex-direction: column; align-items: center; gap: 0.75rem;
  }
  .upload-dropzone:hover, .upload-dropzone:focus { border-color: var(--mid); }
  .upload-dropzone.is-dragging { border-color: var(--red); background: rgba(192, 57, 43, 0.05); }
  .upload-dropzone-icon { font-size: 2.5rem; line-height: 1; }
  .upload-dropzone-text { display: flex; flex-direction: column; gap: 0.25rem; }
  .upload-dropzone-text strong {
    font-family: var(--font-display); text-transform: uppercase;
    font-size: 0.95rem; letter-spacing: 0.06em; color: var(--text);
  }
  .upload-dropzone-text span { font-size: 0.85rem; color: var(--mid); }

  .upload-file-list {
    list-style: none; padding: 0; margin: 1.25rem 0 0;
    display: flex; flex-direction: column; gap: 0.5rem;
  }
  .upload-file-item {
    display: grid; grid-template-columns: 56px 1fr auto;
    gap: 0.85rem; align-items: start;
    background: white; padding: 0.65rem;
    border-radius: var(--radius); border: 1px solid var(--border);
  }
  .upload-file-item.is-uploaded { border-color: rgba(46, 125, 50, 0.4); }
  .upload-file-item.is-failed   { border-color: rgba(192, 57, 43, 0.4); background: rgba(192, 57, 43, 0.03); }

  .upload-thumb {
    width: 56px; height: 56px; background: var(--bg);
    border-radius: var(--radius); overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    font-family: var(--font-display); font-size: 0.7rem;
    color: var(--mid); font-weight: 500;
  }
  .upload-thumb img { width: 100%; height: 100%; object-fit: cover; }

  .upload-info { min-width: 0; }
  .upload-name {
    font-weight: 500; white-space: nowrap; overflow: hidden;
    text-overflow: ellipsis; color: var(--text);
  }
  .upload-sub { font-size: 0.8rem; color: var(--mid); margin-top: 0.15rem; }
  .upload-err { color: var(--red); }
  .upload-ok  { color: #2e7d32; }

  .upload-caption {
    width: 100%;
    margin-top: 0.5rem;
    font-family: var(--font-sub);
    font-size: 0.9rem;
    padding: 0.5rem 0.65rem;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--bg);
    color: var(--text);
    box-sizing: border-box;
    transition: border-color 0.15s, background 0.15s;
  }
  .upload-caption::placeholder { color: var(--muted); }
  .upload-caption:focus {
    outline: none;
    border-color: var(--mid);
    background: white;
  }
  .upload-caption:disabled { opacity: 0.5; cursor: not-allowed; }

  .upload-progress {
    height: 4px; background: var(--border); border-radius: var(--radius);
    overflow: hidden; margin-top: 0.5rem;
  }
  .upload-progress > div { height: 100%; background: var(--red); transition: width 0.2s; }

  .upload-remove {
    background: transparent; border: 1px solid var(--border); border-radius: var(--radius);
    width: 36px; height: 36px; cursor: pointer; color: var(--mid);
    font-size: 1rem; line-height: 1; align-self: start;
  }
  .upload-remove:hover:not(:disabled), .upload-remove:focus:not(:disabled) {
    color: var(--red); border-color: var(--red);
  }
  .upload-remove:disabled { opacity: 0.4; cursor: not-allowed; }

  .upload-summary {
    margin-top: 1.25rem; padding: 0.85rem 1rem;
    border-radius: var(--radius); font-size: 0.95rem;
  }
  .upload-summary.is-success {
    background: rgba(46, 125, 50, 0.08); color: #1b5e20;
    border-left: 3px solid #2e7d32;
  }
  .upload-summary.is-partial {
    background: rgba(245, 124, 0, 0.08); color: #b45200;
    border-left: 3px solid #f57c00;
  }

  .upload-actions { display: flex; gap: 0.75rem; margin-top: 1.25rem; flex-wrap: wrap; }
  .upload-submit {
    background: var(--red); color: white; border: none; border-radius: var(--radius);
    padding: 0.95rem 2rem;
    font-family: var(--font-display); font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.06em; font-size: 0.9rem;
    cursor: pointer; min-height: 48px; transition: background 0.15s;
  }
  .upload-submit:hover:not(:disabled), .upload-submit:focus:not(:disabled) { background: var(--red-dark); }
  .upload-submit:disabled { background: var(--mid); cursor: not-allowed; }
  .upload-clear {
    background: white; color: var(--mid); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 0.95rem 1.5rem;
    font-family: var(--font-display); font-weight: 500;
    text-transform: uppercase; letter-spacing: 0.06em; font-size: 0.85rem;
    cursor: pointer; min-height: 48px;
  }
  .upload-clear:hover, .upload-clear:focus { color: var(--text); border-color: var(--mid); }

  @media (max-width: 640px) {
    .upload-panel { padding: 1.25rem; }
    .upload-title { font-size: 1.3rem; }
    .upload-dropzone { padding: 2rem 0.75rem; }
    .upload-dropzone-icon { font-size: 2rem; }
    .upload-controls { gap: 0.75rem; }
    .upload-file-item { grid-template-columns: 48px 1fr auto; gap: 0.6rem; padding: 0.5rem; }
    .upload-thumb { width: 48px; height: 48px; }
    .upload-actions { flex-direction: column; }
    .upload-submit, .upload-clear { width: 100%; }
  }
`;
